import { ColorToken } from "../../../model/tokens/SDKColorToken";
import { sureOf } from "../../../utils/CommonUtils";
import { TokenJSONElementTransformer } from "./SDKToolsJSONElementTransformer";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Token transformer that allow generic transform of tokens to different destinations, like Style Dictionary or Figma Tokens representation */
export class TokenJSONElementStyleDictionaryTransformer extends TokenJSONElementTransformer {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token Value Representation
    /** Represent color token value either as reference or as plain representation */
    representColorTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = ColorToken.colorValueToHex8(value);
        }
        return result;
    }
    /** Represent dimension token value either as reference or as plain representation */
    representDimensionTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                measure: {
                    type: "size",
                    value: value.measure,
                },
                unit: {
                    type: "string",
                    value: value.unit.toLowerCase(),
                },
            };
        }
        return result;
    }
    /** Represent text token value either as reference or as plain representation */
    representStringTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = value.text;
        }
        return result;
    }
    representOptionTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // TODO: Raw value or complex like dimension
            result = value.value;
        }
        return result;
    }
    /** Represent typography token value either as reference or as plain representation */
    representTypographyTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                fontSize: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.fontSize, allTokens, allGroups, options),
                },
                textDecoration: value.textDecoration,
                textCase: value.textCase,
                letterSpacing: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.letterSpacing, allTokens, allGroups, options),
                },
                paragraphIndent: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.paragraphIndent, allTokens, allGroups, options),
                },
                paragraphSpacing: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.paragraphSpacing, allTokens, allGroups, options),
                },
                lineHeight: value.lineHeight
                    ? {
                        type: "measure",
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        value: this.representDimensionTokenValue(value.lineHeight, allTokens, allGroups, options),
                    }
                    : undefined,
            };
        }
        return result;
    }
    /** Represent border token value either as reference or as plain representation */
    representBorderTokenValue(value, allTokens, allGroups, options) {
        let result;
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                color: {
                    type: "color",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representColorTokenValue(value.color, allTokens, allGroups, options),
                },
                width: {
                    type: "measure",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representDimensionTokenValue(value.width, allTokens, allGroups, options),
                },
                position: {
                    type: "string",
                    value: value.position,
                },
            };
        }
        return result;
    }
    /** Represent shadow token value either as reference or as plain representation */
    representShadowTokenValue(values, allTokens, allGroups, options) {
        let result;
        const value = sureOf(values[0]); // FIXME: Layers
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                color: {
                    type: "color",
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value: this.representColorTokenValue(value.color, allTokens, allGroups, options),
                },
                x: {
                    type: "measure",
                    value: value.x,
                },
                y: {
                    type: "measure",
                    value: value.y,
                },
                radius: {
                    type: "measure",
                    value: value.radius,
                },
                spread: {
                    type: "measure",
                    value: value.spread,
                },
                opacity: {
                    type: "size",
                    value: value.opacity,
                },
            };
        }
        return result;
    }
    /** Represent gradient token value either as reference or as plain representation */
    representGradientTokenValue(values, allTokens, allGroups, options) {
        let result;
        const value = sureOf(values[0]); // FIXME: Layers
        if (value.referencedTokenId) {
            // Forms reference
            result = this.referenceWrapper(this.referenceName(value.referencedTokenId, allTokens, allGroups, options));
        }
        else {
            // Raw value
            result = {
                to: {
                    type: "point",
                    value: {
                        x: {
                            type: "size",
                            value: value.to.x,
                        },
                        y: {
                            type: "size",
                            value: value.to.y,
                        },
                    },
                },
                from: {
                    type: "point",
                    value: {
                        x: {
                            type: "size",
                            value: value.from.x,
                        },
                        y: {
                            type: "size",
                            value: value.from.y,
                        },
                    },
                },
                type: {
                    type: "string",
                    value: value.type,
                },
                aspectRatio: {
                    type: "size",
                    value: value.aspectRatio,
                },
                stops: {},
            };
            // Inject gradient stops
            let count = 0;
            for (const stop of value.stops) {
                const stopObject = {
                    type: "gradientStop",
                    position: {
                        type: "size",
                        value: stop.position,
                    },
                    color: {
                        type: "color",
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        value: this.representColorTokenValue(stop.color, allTokens, allGroups, options),
                    },
                };
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                result.stops[`${count}`] = stopObject;
                count += 1;
            }
        }
        return result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Object wrappers
    /** Retrieve wrapper to certain token (referenced by name) pointing to token value */
    referenceWrapper(reference) {
        return `{${reference}.value}`;
    }
    groupIsAbstract(group) {
        return false;
    }
}
